import { Route, Routes, useNavigate } from "react-router-dom";
import { observer, useLocalObservable } from "mobx-react-lite";
import { AppContext } from "core/app-context";
import { Page } from "core/constants";
import { Installation } from "core/models/installation-response";
import TrackingStore from "core/tracking.store";
import CatchAll from "components/catch-all";
import OtpForm from "components/form/otp.form";
import SearchForm from "components/form/search.form";
import InstallationComponent from "components/installations/installation";
import Installations from "components/installations/installations";
import FourSureService from "services/foursure.service";

const onPageChange = (page: string) => {
  const eventName = "track-my-install-page-change";

  const event = new CustomEvent(eventName, {
    detail: page
  })

  document.dispatchEvent(event);
}

const App = observer(() => {
  const navigate = useNavigate();

  const store = useLocalObservable(() => new TrackingStore(onPageChange));

  // addTestData(store, tests.installations);

  const service = new FourSureService();

  const defaultSearchError = "The number supplied could not be found.";

  const find = async () => {
    store.updateIsLoading(true);

    try {
      const response = await service.findAsync(store.searchDetails.searchValue);

      const data = response.data;

      store.updateSearchError(data.success ? undefined : data?.reason ?? defaultSearchError);

      if (data.success) {
        store.updateOtpLastSent(new Date());
        navigate(Page.otp);
      } else {
        navigate(Page.search);
      }
    } catch (error) {
      store.updateSearchError("Sorry there has been a problem. Please try again.");
      console.error("Error searching for installation", error);
    }

    store.updateIsLoading(false);
  };

  const validate = async () => {
    store.updateIsLoading(true);

    try {
      const response = await service.validateAsync(store.searchDetails.searchValue, store.otpDetails.otp);

      const data = response.data;

      store.updateOtpError(data.success ? undefined : data.reason);

      if (data.success) {
        store.updateInstallations(data.payload);
        navigate(Page.installations);
      }
    } catch (error) {
      store.updateOtpError("Sorry there has been a problem validating your OTP. Please try again.");
      console.error("Error validating OTP.", error);
    }

    store.updateIsLoading(false);
  }

  const select = (installation: Installation) => {
    store.updateInstallation(installation);
    navigate(Page.installation(installation.customer_ref));
  };

  const title = "Track My Installation" + (store.page == "installation" ? "" : "s");

  const path = (page: string) => page.startsWith("/") ? page.substring(1) : page;

  return (
    <AppContext.Provider value={store}>
      <section className={path(store.page)}>
        <h2>{title}</h2>

        <Routes>
          <Route
            path={path(Page.search)}
            element={<SearchForm onSubmit={find} />} />

          <Route
            path={path(Page.otp)}
            element={
              <OtpForm
                onResend={find}
                onSubmit={validate} />} />

          <Route
            path={path(Page.installation(":reference"))}
            element={
              <InstallationComponent
                onBack={() => navigate(Page.installations)} />
            } />

          <Route
            path={path(Page.installations)}
            element={
              <Installations
                onSelect={select}
                onBack={() => navigate(Page.search)} />
            } />

          <Route
            path="*"
            element={<CatchAll onProcess={() => navigate(Page.search)} />} />
        </Routes>
      </section>
    </AppContext.Provider>
  );
});

export default App;
